import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LandingMobileNav from './LandingMobileNav';
import lanerunnerLogoBlack from '@/assets/lanerunner-logo-black.png';
import lanerunnerLogoWhite from '@/assets/lanerunner-logo-white.png';

interface LandingHeaderProps {
  activePage?: 'home' | 'shippers' | 'brokers' | 'carriers' | 'partner-program' | 'pricing' | 'lr-direct' | 'load-board';
  /**
   * 'frosted' = homepage-only treatment: transparent bar over the dark hero
   * that scrolls away with the page (not sticky).
   * Defaults to the original solid white bar used by every other marketing page.
   */
  variant?: 'default' | 'frosted';
}

const LandingHeader: React.FC<LandingHeaderProps> = ({ activePage, variant = 'default' }) => {
  const navLinks = [
    { label: 'HOME', href: '/', key: 'home' },
    { label: 'LOADBOARD', href: '/load-board', key: 'load-board' },
    { label: 'SHIPPERS', href: '/shippers', key: 'shippers' },
    { label: 'BROKERS', href: '/brokers', key: 'brokers' },
    { label: 'CARRIERS', href: '/carriers', key: 'carriers' },
    { label: 'PARTNERS', href: '/partner-program', key: 'partner-program' },
    { label: 'PRICING', href: '/pricing', key: 'pricing' },
  ];


  // Homepage variant: transparent bar sitting over the dark hero. It is NOT
  // sticky — it scrolls away with the page like any other content.
  const isTransparent = variant === 'frosted';

  const navClass = isTransparent
    ? 'absolute top-0 left-0 right-0 z-50 bg-transparent border-b border-transparent shadow-none'
    : 'absolute top-0 left-0 right-0 z-50 bg-white shadow-sm';

  return (
    <nav className={navClass}>
      <div className="w-full px-4 sm:px-6 py-5">
        <div className="flex items-center">
          {/* Left: Mobile menu + Logo */}
          <div className="flex items-center flex-shrink-0">
            <LandingMobileNav
              onLogin={() => window.location.href = '/login'}
              onSignup={() => window.location.href = '/pricing'}
              triggerClassName={isTransparent ? 'text-white hover:bg-white/10' : undefined}
            />
            <Link to="/" className="flex items-center">
              <img
                src={isTransparent ? lanerunnerLogoWhite : lanerunnerLogoBlack}
                alt="LaneRunner Logo"
                className="h-8 lg:h-8 xl:h-10"
              />
            </Link>
          </div>

          {/* Center: Nav links */}
          <div
            className={`hidden lg:flex flex-1 items-center justify-center min-w-0 gap-2 xl:gap-6 text-sm font-semibold tracking-wide transition-colors duration-300 ${
              isTransparent ? 'text-white/90' : 'text-gray-900'
            }`}
          >
            {navLinks.filter((link) => link.key !== 'home').map((link) => {
              const isActive = activePage === link.key;
              const activeColor = isTransparent ? 'text-[#3ED9BE]' : 'text-lr-cyan';
              const hoverColor = isTransparent ? 'hover:text-[#3ED9BE]' : 'hover:text-lr-cyan';

              return (
                <Link
                  key={link.key}
                  to={link.href}
                  className={`whitespace-nowrap transition-colors ${isActive ? activeColor : hoverColor}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Right: Auth buttons */}
          <div className="hidden lg:flex items-center flex-shrink-0 gap-2">
            <Link
              to="/login"
              className={`rounded-full border transition-colors text-sm font-semibold px-3 py-2 whitespace-nowrap ${
                isTransparent
                  ? 'border-white/25 text-white hover:bg-white/10 hover:border-white/45'
                  : 'border-[#02857E]/40 text-[#02857E] hover:bg-[#02857E]/10 hover:border-[#02857E]'
              }`}
            >
              LOGIN
            </Link>
            <Button
              asChild
              className="rounded-full bg-[#02857E] text-white font-semibold hover:bg-[#026d67] text-sm px-4 shadow-[0_4px_14px_-4px_rgba(2,133,126,0.45)] hover:shadow-[0_6px_18px_-4px_rgba(2,133,126,0.55)] transition-all whitespace-nowrap"
            >
              <Link to="/pricing">SIGN UP →</Link>
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default LandingHeader;
